import React from 'react';
import { cn } from '../../../lib/utils';
import type { FilterOption } from '../types';

interface CardRadioGroupProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export const CardRadioGroup: React.FC<CardRadioGroupProps> = ({
  value,
  onChange,
  options,
  label,
  description,
  disabled = false,
  className,
}) => {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {(label || description) && (
        <div className="flex flex-col gap-1">
          {label && (
            <label className="text-sm font-medium text-text-secondary">{label}</label>
          )}
          {description && (
            <p className="text-xs text-text-muted">{description}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={disabled}
              className={cn(
                'relative flex items-start gap-3 p-3 rounded-lg border transition-all',
                'focus:outline-none focus:ring-2 focus:ring-accent-primary',
                isSelected
                  ? 'bg-accent-primary/10 border-accent-primary text-text-primary'
                  : 'bg-surface-container border-border-subtle text-text-secondary hover:border-accent-primary/50 hover:bg-surface-container-high',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div
                className={cn(
                  'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                  isSelected
                    ? 'border-accent-primary'
                    : 'border-border-subtle'
                )}
              >
                {isSelected && (
                  <div className="w-2.5 h-2.5 rounded-full bg-accent-primary" />
                )}
              </div>

              <div className="flex flex-col items-start gap-0.5 min-w-0">
                <span className="text-sm font-medium text-left truncate w-full">
                  {option.label}
                </span>
                {option.subtitle && (
                  <span className="text-xs text-text-muted truncate w-full">
                    {option.subtitle}
                  </span>
                )}
              </div>

              {option.badge && (
                <span className="absolute top-2 right-2 text-xs font-medium px-1.5 py-0.5 rounded bg-amber-700 text-black" title={option.badge}>
                  {option.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CardRadioGroup;
